import { defineComponent, ref, toRef, watchEffect } from 'vue'
import {
  Col,
  Form,
  Input,
  InputPassword,
  InputNumber,
  Cascader,
  Select,
  TimePicker,
  DatePicker,
  TreeSelect,
  Row,
  Switch,
  Tooltip,
  Radio,
  Checkbox
} from 'ant-design-vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'
import { DateTimeRangePicker } from '-/Base/DateTimeRangePicker'
import { BdFormProps } from './props'
import { useBdForm } from './hooks/useBdForm'
import styles from './index.module.less'
import type { ISchemaItem } from './types'
import { isObject } from '@bd/vue3-utils'
export default defineComponent({
  name: 'BdForm',
  props: BdFormProps,
  setup(props, { expose }) {
    const formRef = ref()
    const formWrapRef = ref()
    const { initForm, modelRef, resetFields, rulesRef, formSchema, feedbackVisible, dictMap, dictOriginalMap, executeDictQueue } = useBdForm(
      props.schema
    )
    initForm()
    const initFields = () => {
      if (isObject(props.model)) {
        Object.keys(modelRef).forEach(key => {
          if (props.model[key] !== undefined && props.model[key] !== null) modelRef[key] = props.model[key]
        })
      }
    }
    if (props.watch) {
      watchEffect(() => {
        initFields()
      })
    }
    expose({
      validate: () => formRef.value?.validate(),
      clearValidate: () => formRef.value?.clearValidate(),
      executeDictQueue,
      resetFields,
      initFields,
      modelRef
    })
    const getType = (schema: ISchemaItem) => {
      const { type, dataIndex } = schema
      const _baseProps = {
        placeholder: schema.placeholder,
        disabled: typeof schema.disabled === 'function' ? schema.disabled({ modelRef, record: toRef(modelRef, dataIndex) }) : schema.disabled,
        ...schema.attrs
      }
      const _selectBaseProps = {
        placeholder: schema.placeholder,
        showSearch: true,
        allowClear: true,
        height: 400,
        disabled:
          (typeof schema.disabled === 'function' ? schema.disabled({ modelRef, record: toRef(modelRef, dataIndex) }) : schema.disabled) ||
          !dictMap.value.has(dataIndex),
        ...schema.attrs
      }
      if (type === 'textarea') {
        return <Input.TextArea v-model={[modelRef[dataIndex], 'value']} autoSize={{ minRows: 1, maxRows: 6 }} {..._baseProps} />
      } else if (type === 'number') {
        return <InputNumber v-model={[modelRef[dataIndex], 'value']} {..._baseProps} />
      } else if (type === 'boolean') {
        return <Switch v-model={[modelRef[dataIndex], 'checked']} {...schema.attrs} />
      } else if (type === 'numberSwitch') {
        return <Switch v-model={[modelRef[dataIndex], 'checked']} checkedValue={1} unCheckedValue={0} {...schema.attrs} />
      } else if (type === 'select' || type === 'selects') {
        return (
          <Select
            v-model={[modelRef[dataIndex], 'value']}
            mode={schema?.attrs?.mode ?? schema.type === 'selects' ? 'multiple' : undefined}
            optionFilterProp='label'
            optionLabelProp='label'
            {..._selectBaseProps}
          >
            {dictMap.value.get(dataIndex)?.map((item, idx) => (
              <Select.Option key={item.value} label={item.label}>
                {schema.option ? schema.option({ record: item, origin: dictOriginalMap.value.get(dataIndex)?.[idx] }) : item.label}
              </Select.Option>
            ))}
          </Select>
        )
      } else if (type === 'tagsInput') {
        return <Select v-model={[modelRef[dataIndex], 'value']} mode='tags' className={styles.tagsInput} {..._baseProps} />
      } else if (type === 'treeSelect') {
        return (
          <TreeSelect
            v-model={[modelRef[dataIndex], 'value']}
            treeData={dictMap.value.get(dataIndex)}
            treeLine={{ showLeafIcon: false }}
            treeNodeFilterProp='label'
            treeDefaultExpandAll
            fieldNames={{
              label: 'label',
              key: 'value',
              value: 'value'
            }}
            {..._selectBaseProps}
          />
        )
      } else if (type === 'treeSelects') {
        return (
          <TreeSelect
            v-model={[modelRef[dataIndex], 'value']}
            treeData={dictMap.value.get(dataIndex)}
            treeCheckable={true}
            treeLine={{ showLeafIcon: false }}
            treeNodeFilterProp='label'
            treeDefaultExpandAll
            showCheckedStrategy={TreeSelect.SHOW_ALL}
            fieldNames={{
              label: 'label',
              key: 'value',
              value: 'value'
            }}
            {..._selectBaseProps}
          />
        )
      } else if (type === 'time') {
        return (
          <TimePicker v-model={[modelRef[dataIndex], 'value']} valueFormat={schema?.attrs?.valueFormat ?? 'HH:mm:ss'} allowClear {..._baseProps} />
        )
      } else if (type === 'date' || type === 'dateTime') {
        return (
          <DatePicker
            v-model={[modelRef[dataIndex], 'value']}
            valueFormat={schema?.attrs?.valueFormat ?? type === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'}
            allowClear
            showTime={type === 'dateTime'}
            {..._baseProps}
          />
        )
      } else if (type === 'dateMonth') {
        return <DatePicker.MonthPicker v-model={[modelRef[dataIndex], 'value']} mode='month' valueFormat='YYYY-MM' allowClear {..._baseProps} />
      } else if (type === 'dateTimeRangePicker') {
        return <DateTimeRangePicker v-model={[modelRef[dataIndex], 'value']} {...schema.attrs} />
      } else if (type === 'dateRange' || type === 'dateTimeRange') {
        //! todo:设置日期默认值
        const valueFormat = schema?.attrs?.valueFormat ?? type === 'dateRange' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'
        return (
          <DatePicker.RangePicker
            v-model={[modelRef[dataIndex], 'value']}
            // defaultValue={schema.default ? [moment(1625852272652, valueFormat), moment(1625852272652, valueFormat)] : undefined}
            showTime={type === 'dateTimeRange'}
            valueFormat={valueFormat}
            allowClear
            disabled={schema.disabled}
            {...schema.attrs}
          />
        )
      } else if (type === 'timeRange') {
        return (
          <TimePicker.TimeRangePicker
            v-model={[modelRef[dataIndex], 'value']}
            valueFormat='HH:mm:ss'
            allowClear
            disabled={schema.disabled}
            {...schema.attrs}
          />
        )
      } else if (type === 'cascader') {
        return (
          <Cascader
            v-model={[modelRef[dataIndex], 'value']}
            expandTrigger='hover'
            options={dictMap.value.get(dataIndex)}
            fieldNames={{
              label: 'label',
              value: 'value',
              children: 'children'
            }}
            {..._selectBaseProps}
          />
        )
      } else if (type === 'cascaders') {
        return (
          <Cascader
            v-model={[modelRef[dataIndex], 'value']}
            expandTrigger='hover'
            multiple
            options={dictMap.value.get(dataIndex)}
            fieldNames={{
              label: 'label',
              value: 'value',
              children: 'children'
            }}
            {..._selectBaseProps}
          />
        )
      } else if (type === 'checkbox') {
        return <Checkbox.Group name={dataIndex} v-model={[modelRef[dataIndex], 'value']} options={dictOriginalMap.value.get(dataIndex)} />
      } else if (type === 'radio') {
        return <Radio.Group name={dataIndex} v-model={[modelRef[dataIndex], 'value']} options={dictOriginalMap.value.get(dataIndex)} />
      } else if (type === 'numberRadio') {
        return (
          <Radio.Group name={dataIndex} v-model={[modelRef[dataIndex], 'value']}>
            <Radio value={0}>{schema.attrs?.text[0] ?? '否'}</Radio>
            <Radio value={1}>{schema.attrs?.text[1] ?? '是'}</Radio>
          </Radio.Group>
        )
      } else if (type === 'booleanRadio') {
        return (
          <Radio.Group name={dataIndex} v-model={[modelRef[dataIndex], 'value']}>
            <Radio value={false}>{schema.attrs?.text[0] ?? '否'}</Radio>
            <Radio value={true}>{schema.attrs?.text[1] ?? '是'}</Radio>
          </Radio.Group>
        )
      } else if (type === 'password') {
        return <InputPassword v-model={[modelRef[dataIndex], 'value']} {..._baseProps} />
      } else {
        return <Input v-model={[modelRef[dataIndex], 'value']} {..._baseProps} />
      }
    }
    const renderComponent = () => {
      return formSchema.map(
        item =>
          item.show && (
            <Col {...(item.col || props.col)} class='bd-form__item'>
              {item.type !== 'block' ? (
                <Form.Item
                  name={item.dataIndex}
                  has-feedback={feedbackVisible.has(item.type)}
                  label={
                    item.title ? (
                      <div class={[styles.item, styles[props.layout]]}>
                        <div class={styles.label}>
                          {item.title}
                          {item.help && (
                            <Tooltip placement='top' title={item.help}>
                              <QuestionCircleOutlined class={styles.help} />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    ) : undefined
                  }
                >
                  {item.render ? item.render({ modelRef, record: toRef(modelRef, item.dataIndex) }) : getType(item)}
                </Form.Item>
              ) : (
                item.render({ modelRef })
              )}
            </Col>
          )
      )
    }
    return () => (
      <div class={styles.wrap} ref={formWrapRef}>
        <Form
          ref={formRef}
          class={styles.form}
          layout={props.layout}
          scrollToFirstError={props.scrollToFirstError}
          model={modelRef}
          rules={rulesRef}
          {...props.attrs}
        >
          <Row {...props.row}>{renderComponent()}</Row>
        </Form>
      </div>
    )
  }
})
